<template>
    <wrapper-features-templates-operations
    :fullSizeContent="activeStep !== 1"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showStepper="renderStep1 && (activeStep <= stepperParams.length)"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :feedbackOperationData="feedbackOperationData"
    :showFooter="renderStep1 && !applyErrorState && (activeStep !== 4)"
    :footerButtonsParams="footerButtonsData.filter(ele => !ele.hidden)"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('payments.batchfile.newPayment.tokenModalTitle')"
    :showFloatingButton="!showLoader && renderStep1 && !applyErrorState && (activeStep === 1 || activeStep === 2)"
    :floatingButtonParams="{ icon: 'help', label: t('payments.batchfile.newPayment.help')}"
    @floatingButtonClicked="showHelpModal = true"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, requestBatchTransfer, true)">
        <component
        v-if="renderStep1 && !applyErrorState"
        :is="activeStepComponent"
        :paymentType="paymentType"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :batchfileEntryError="batchfileEntryError"
        :clearForm="clearForm"
        :batchfileEntries="batchfileEntries"
        :responseData="responseData"
        :isFavorite="isFavorite"
        :entryTableReset="entryTableReset"
        @updatePaymentType="updatePaymentTypeHandler"
        @updateFormData="updateFormDataHandler"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formIsCleared="clearForm = false"
        @openReuseModal="showReuseModal = true"
        @fileEntryValueChanged="fileEntryValueChangedHandler"
        @addNewFileEntry="addNewFileEntryHandler"
        @fileEntryOnBlur="fileEntryOnBlurHandler"
        @removeEntry="removeEntryHandler"
        @showFavoriteModal="showFavoritesModal = true"
        @newPayment="newPaymentHandler"/>
    </wrapper-features-templates-operations>
    <!-- Helper Modal -->
    <helper-modal
    v-if="showHelpModal"
    helperStepComponentsFolder="features/payments/batchfile/newPayment/helperModalSteps"
    :titlesArray="helperModalTopics"
    :helpTopics="helperModalTopics"
    @hideMe="showHelpModal = false"/>
    <!-- Reuse Payment Modal -->
    <reuse-payment-modal
    v-if="showReuseModal"
    @hideMe="showReuseModal = false"
    @reuseSelectedPayment="requestPaymentDetail"/>
    <!-- Return to Step 1 Modal Alert -->
    <modal-centered-generic
    v-if="showPreviousStepModal"
    negative
    rightButtonIcon="arrow-right-full"
    :title="t('payments.batchfile.newPayment.previousStepModal.title')"
    :text="t('payments.batchfile.newPayment.previousStepModal.text')"
    :leftButtonText="t('payments.batchfile.newPayment.previousStepModal.leftButton')"
    :rightButtonText="t('payments.batchfile.newPayment.previousStepModal.rightButton')"
    @hideMe="showPreviousStepModal = false"
    @rightButtonClicked="[clearAllFields(), activeStep--, showPreviousStepModal = false]"/>
    <!-- Favorites modal -->
    <favorites-modal
    v-if="showFavoritesModal"
    :modalParams="{ operationId: responseData.approveStepCustomerOperation.id, type: isFavorite ? 'remove' : 'create' }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler($event)"
    @requestError="favoriteModalErrorHandler($event)"/>
    <!-- Error Feedback -->
    <feedback-toast
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ReusePaymentModal from '@/components/features/payments/batchfile/newPayment/ReusePaymentModal'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import FeedbackToast from '@/components/generic/toasts/Generic'
import HelperModal from '@/components/generic/modals/side/helper/HelperModal'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { clearObject, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { emailValidator } from '@/assets/scripts/validators'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { createBatchfileDownloadReceiptUrl, createBatchfileExcelDownloadUrl } from '@/network/api/batchfile.api.js'
import { emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestAccountsBalances,
    requestBatchfileTransferDetail,
    requestBatchfileOperationTypes,
    requestIbanInformation,
    submitBatchfileXLS,
    submitManualBatchFileHeader,
    submitManualBatchFileEntries,
    aproveBatchfileTranfer,
    clearFeatureStates
} = mapActions(moduleMapper.PAYMENTS_BATCHFILE_NEW_PAYMENT)

const {
    getAccountsBalances,
    getBatchfileOperationTypes
} = mapGetters(moduleMapper.PAYMENTS_BATCHFILE_NEW_PAYMENT)

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const renderStep1 = ref(false)
const showReuseModal = ref(false)
const showHelpModal = ref(false)
const showFavoritesModal = ref(false)

/*************************************************
*                                                *
*                  Helper Modal                  *
*                                                *
*************************************************/
const helperModalTopics = ref([
    t('payments.batchfile.newPayment.helperModal.index.topic1'),
    t('payments.batchfile.newPayment.helperModal.index.topic2'),
    t('payments.batchfile.newPayment.helperModal.index.topic3'),
    t('payments.batchfile.newPayment.helperModal.index.topic4'),
    t('payments.batchfile.newPayment.helperModal.index.topic5'),
    t('payments.batchfile.newPayment.helperModal.index.topic6'),
    t('payments.batchfile.newPayment.helperModal.index.topic7'),
    t('payments.batchfile.newPayment.helperModal.index.topic8'),
    t('payments.batchfile.newPayment.helperModal.index.topic9')
])

/*************************************************
*                                                *
*                  Steps Logic                   *
*                                                *
*************************************************/
const activeStep = ref(1)
const showPreviousStepModal = ref(false)

const stepperParams = ref([
    { description: t('payments.batchfile.newPayment.step1.title') },
    { description: t('payments.batchfile.newPayment.step2.title') },
    { description: t('payments.batchfile.newPayment.step3.title') }
])

const activeStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/payments/batchfile/newPayment/Step' + stepNumber))
})

/*************************************************
*                                                *
*                   Form Data                    *
*                                                *
*************************************************/
const scrollValue = ref(null)
const paymentType = ref('manual')

const formData = ref({
    /* Step 1 */
    batchfile: '',
    fileType: '',
    fileBlob: '',
    fileName: '',
    companyCode: '',
    /* Step 2 */
    selectedAccountId: null,
    currency: '',
    transferDescription: '',
    operationType: '',
    processDate: '',
    reference: ''
})

const batchfileEntry = ref({
    iban: '',
    name: '',
    amount: '',
    transferReference: '',
    email: ''
})

const batchfileEntryError = ref([{
    iban: false,
    invalidIban: false,
    name: false,
    amount: false,
    transferReference: false,
    email: false
}])

const batchfileEntries = ref([])
const responseFileData = ref(null)
const applyErrorOnDate = ref(false)
const invalidRowsData = ref(false)
const batchFileSubmissionId = ref(null)
const batchFileSubmissionEntriesResponse = ref(null)

const mandatoryList = ref([])

const showMandatoryErrors = ref(false)

const clearForm = ref(false)

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

const updatePaymentTypeHandler = event => {
    paymentType.value = event
    clearFileHandler()
}

const clearFileHandler = () => {
    formData.value = clearObject(formData.value)
    formData.value.selectedAccountId = getAccountsBalances.value.find(account => { return account.default }).id
    formData.value.currency = getAccountsBalances.value.find(account => account.default).currency

    clearForm.value = true
}

const buildFormData = () => {
    /* form data init (this is going to be the multiPart) */
    const fileForm = new FormData()
    /* FILE */
    fileForm.append('file', formData.value.fileBlob, formData.value.fileName)
    fileForm.append('fileName', formData.value.fileName)
    fileForm.append('fileType', formData.value.fileType)
    fileForm.append('forValidationOnly', true)
    fileForm.append('ignoreCompanyCode', true)
    return fileForm
}

const createManualFileHandler = () => {
    if (batchfileEntries.value.length === 1) {
        addNewFileEntryHandler()
    } else if (!batchfileEntries.value.length) {
        addNewFileEntryHandler()
        addNewFileEntryHandler()
    }
    activeStep.value++
}

const removeEntryHandler = rowId => {
    batchfileEntries.value.splice(rowId, 1)
    batchfileEntryError.value.splice(rowId, 1)

    validateRowsData()
}

const handleFileResponse = reutilizeFile => {
    formData.value.selectedAccountId = getAccountsBalances.value.find(account => { return account.accountNumber === responseFileData.value.internalAccountNumber }).id
    formData.value.transferDescription = responseFileData.value.transferDescription || ''
    formData.value.currency = responseFileData.value.currency
    formData.value.operationType = ''

    /* Validating if our received code is one of the possible options */
    const receivedTypeCode = responseFileData.value.operationTypeCodeHeader

    getBatchfileOperationTypes.value.forEach(item => {
        if (receivedTypeCode && (parseInt(item.code) === parseInt(receivedTypeCode))) {
            formData.value.operationType = item.code.toString()
        }
    })

    if (responseFileData.value.desiredProcessingDate) {
        const desiredProcessingDate = new Date(responseFileData.value.desiredProcessingDate)
        const todayDate = new Date()

        const parsedDesiredDate = new Date(desiredProcessingDate.getFullYear(), desiredProcessingDate.getMonth(), desiredProcessingDate.getDate(), 0, 0, 0)
        const parsedTodayDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate(), 0, 0, 0)

        if (responseFileData.value.desiredProcessingDate && parsedDesiredDate < parsedTodayDate) {
            applyErrorOnDate.value = true
        } else formData.value.processDate = parsedDesiredDate
    } else formData.value.processDate = ''

    formData.value.reference = responseFileData.value.sourceReference || ''

    if (responseFileData.value.fileEntries.length) batchfileEntries.value = fileEntriesHandler(responseFileData.value.fileEntries)

    const errorList = batchfileEntryError.value

    for (let i = 0; i < errorList.length; i++) {
        while (errorList.length < batchfileEntries.value.length) addNewFileEntryErrorHandler()
    }

    validateRowsData()

    if (reutilizeFile) {
        // Handling of success and error is made on requestIbanInformationHandler
        Promise.all([batchfileEntries.value.forEach((row, index) => { requestIbanInformationHandler(index) })])
    }

    showOverlayLoader.value = false
    activeStep.value++
}

const checkIfRowIsEmpty = object => {
	for (var key in object) {
		if (!object[key] || (object[key].length === 0) || object[key] === 0) return true
	}
	return false
}

// Validate Rows Data
const validateRowsData = () => {
    invalidRowsData.value = false
    batchfileEntries.value.forEach(row => {
        const temp = { ...row }
        /* First we check if mdw error is provided */
        if (temp.mdwErrorCode) invalidRowsData.value = true
        /* Then we remove this temp fields to check if any params is empty */
        delete temp.errorCode
        delete temp.errorMessage
        delete temp.mdwErrorCode
        delete temp.email // Not a Mandatory Field
        delete temp.validatedIban

        /* Validating if any field is empty */
        if (checkIfRowIsEmpty(temp)) {
            invalidRowsData.value = true
        }

        /* Clearing errorMessage */
        if (!invalidRowsData.value && !row.mdwErrorCode) row.errorMessage = null
    })
}

const fileEntriesHandler = fileEntries => {
    return fileEntries.map((obj, index) => {
        // Finding iban to use
        const joinedValues = obj.nibBankCode && obj.nibBranchCode && obj.nibAccountNumber && obj.nibCheckDigit ? `AO06${obj.nibBankCode.trim()}${obj.nibBranchCode.trim()}${obj.nibAccountNumber.trim()}${obj.nibCheckDigit.trim()}` : ''

        const ibanToUse = obj.iban ? obj.iban : joinedValues

        return {
                iban: ibanToUse || '',
                name: obj.beneficiaryName || obj.name || '',
                amount: obj.amount || '',
                transferReference: obj.transferReference || '',
                email: obj.email || '',
                validatedIban: (batchfileEntries.value[index] && batchfileEntries.value[index].validatedIban) ? batchfileEntries.value[index].validatedIban : false,
                mdwErrorCode: obj.errorCode,
                errorCode: obj.errorCode ? obj.errorMessage : '',
                errorMessage: obj.errorCode ? obj.errorMessage : t('payments.batchfile.newPayment.genericErrorMessage')
        }
    })
}
const addNewFileEntryErrorHandler = () => {
    batchfileEntryError.value.push({
        iban: false,
        invalidIban: false,
        name: false,
        amount: false,
        transferReference: false,
        email: false
    })
}

const addNewFileEntryHandler = () => {
    batchfileEntries.value.push({ ...batchfileEntry.value })

    if (batchfileEntries.value.length > 1) addNewFileEntryErrorHandler()

    validateRowsData()
}

const fileEntryOnBlurHandler = ({ inputIdentifier, rowId }) => {
    const isBaiIban = batchfileEntries.value[rowId][inputIdentifier]?.includes('AO06')

    /* Amount Validation */
    if (inputIdentifier === 'amount' && parseInt(batchfileEntries.value[rowId]?.[inputIdentifier]) === 0) {
        batchfileEntryError.value[rowId][inputIdentifier] = true
    }

    /* IBAN Validation */
    if (inputIdentifier === 'iban' && batchfileEntries.value[rowId][inputIdentifier].length < 25 && batchfileEntries.value[rowId][inputIdentifier].length !== 0) {
        batchfileEntryError.value[rowId][inputIdentifier] = true
    } else if (inputIdentifier === 'iban' && isBaiIban && !batchfileEntryError.value[rowId][inputIdentifier]) {
        requestIbanInformationHandler(rowId)
    }
    /* Email Validation */
    if (inputIdentifier === 'email' && !emailValidator(batchfileEntries.value[rowId][inputIdentifier])) {
        batchfileEntryError.value[rowId][inputIdentifier] = true
    }
}

const fileEntryValueChangedHandler = ({ inputIdentifier, inputValue, rowId }) => {
    if (batchfileEntries.value[rowId][inputIdentifier] && batchfileEntries.value[rowId][inputIdentifier] !== inputValue) {
        delete batchfileEntries.value[rowId].mdwErrorCode
    }

    batchfileEntries.value[rowId][inputIdentifier] = inputValue

    batchfileEntryError.value[rowId][inputIdentifier] = false

    if (inputIdentifier === 'iban') batchfileEntryError.value[rowId].invalidIban = false

    validateRowsData()
}

const createFileBody = () => {
    return batchfileEntries.value.map(obj => {
        return {
            iban: obj.iban,
            beneficiaryName: obj.name,
            amount: obj.amount,
            transferReference: obj.transferReference,
            email: obj.email ? obj.email : ''
        }
    })
}

const newPaymentHandler = () => {
    paymentType.value = 'manual'
    activeStep.value = 1
    clearAllFields()
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const validateUploaderInputs = () => {
    if (paymentType.value === 'manual') {
        createManualFileHandler()
    } else atLeastOneIsEmpty(formData.value, ['batchfile']) ? showMandatoryErrors.value = true : submitFileHandler()

    clearForm.value = false
}

const validateBatchFileInfoInputs = () => {
    mandatoryList.value = [
        ...mandatoryList.value,
        'selectedAccountId',
        'fileName',
        'transferDescription',
        'operationType',
        'processDate',
        'reference'
    ]

    const mandatoryListTemp = [
        'iban',
        'name',
        'amount',
        'transferReference',
        'email'
    ]

    var emptyRowEntries = false

    batchfileEntries.value.forEach(entry => {
        if (atLeastOneIsEmpty(entry, mandatoryListTemp)) {
            emptyRowEntries = true

            batchfileEntryError.value.forEach((errorEntry, index) => {
                for (var item in errorEntry) {
                    const input = batchfileEntries.value[index][item]

                    if (!input || input.length === 0 || input === 0) batchfileEntryError.value[index][item] = true
                }
            })
        }
        emptyRowEntries = false
    })

    if (!atLeastOneIsEmpty(formData.value, mandatoryList.value) && !invalidRowsData.value && !emptyRowEntries) {
        submitBatchfileHeader()
    } else showMandatoryErrors.value = true
}

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
    return [{
        label: t('payments.batchfile.newPayment.footerButtons.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value === 3 || activeStep.value === 4 || (activeStep.value === 1 && paymentType.value === 'manual'),
        callbackFunction: () => { clearAllFields() }
    }, {
        label: t('payments.batchfile.newPayment.footerButtons.back'),
        icon: 'arrow-left-full',
        iconPosition: 'left',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { showPreviousStepModal.value = true }
    }, {
        label: t('payments.batchfile.newPayment.footerButtons.forward'),
        buttonType: 'primary',
        icon: 'arrow-right-full',
        buttonPosition: 'right',
        iconPosition: 'right',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { validateUploaderInputs() }
    }, {
        label: t('payments.batchfile.newPayment.footerButtons.goBack'),
        icon: 'arrow-left-full',
        iconPosition: 'left',
        secondVariant: true,
        hidden: activeStep.value !== 3,
        callbackFunction: () => {
            activeStep.value = 2
        }
    }, {
        label: t('payments.batchfile.newPayment.footerButtons.forward'),
        buttonType: 'primary',
        icon: 'arrow-right-full',
        buttonPosition: 'right',
        iconPosition: 'right',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { validateBatchFileInfoInputs() }
    },
    {
        label: t('payments.batchfile.newPayment.footerButtons.validate'),
        buttonType: 'primary',
        icon: 'check-line',
        buttonPosition: 'right',
        iconPosition: 'right',
        hidden: activeStep.value !== 3,
        callbackFunction: () => { requestBatchTransfer() }
    }]
})

const clearAllFields = () => {
    mandatoryList.value = []

    if (activeStep.value === 1) {
        paymentType.value = 'manual'
        clearFileHandler()
    } else {
        formData.value = {
            /* Step 1 */
            batchfile: '',
            fileType: '',
            fileBlob: '',
            fileName: '',
            companyCode: '',
            /* Step 2 */
            selectedAccountId: null,
            currency: '',
            transferDescription: '',
            operationType: '',
            processDate: '',
            reference: ''
        }
    }
    formData.value.selectedAccountId = getAccountsBalances.value.find(account => { return account.default }).id
    formData.value.currency = getAccountsBalances.value.find(account => account.default).currency

    batchfileEntries.value = []
    batchfileEntries.value.push({ ...batchfileEntry.value })
    batchfileEntries.value.push({ ...batchfileEntry.value })
    responseFileData.value = null
    applyErrorOnDate.value = false
    invalidRowsData.value = false
    scrollValue.value = 0
    responseData.value = null
    feedbackOperationData.value = null
    entryTableReset.value = !entryTableReset.value

    batchfileEntryError.value = [{
        iban: false,
        invalidIban: false,
        name: false,
        amount: false,
        transferReference: false,
        email: false
    }]

    batchfileEntryError.value.push({
        iban: false,
        invalidIban: false,
        name: false,
        amount: false,
        transferReference: false,
        email: false
    })

    clearForm.value = true
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialFetchs = async () => {
    cancelAllPendingRequests()

    showLoader.value = true
    renderStep1.value = false
    applyErrorState.value = false

	try {
        await requestAccountsBalances()
        await requestBatchfileOperationTypes()

        /* Setting Initial Selected Account */
		formData.value.selectedAccountId = getAccountsBalances.value.find(account => account.default).id
        formData.value.currency = getAccountsBalances.value.find(account => account.default).currency

        showLoader.value = false
        renderStep1.value = true
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialFetchs)
	}
}

const requestPaymentDetail = async selectedTransfer => {
    showOverlayLoader.value = true

    formData.value.fileName = selectedTransfer.fileName
    formData.value.transferDescription = selectedTransfer.transferDescription

    try {
        const response = await requestBatchfileTransferDetail(selectedTransfer.id)
        responseFileData.value = response.data.data
        handleFileResponse(true)
	} catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.response.data, submitFileHandler)
	}
}

const submitFileHandler = async () => {
    showOverlayLoader.value = true
    const payload = buildFormData()
    try {
        const response = await submitBatchfileXLS(payload)
        responseFileData.value = response.data.data
        handleFileResponse()
	} catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.response.data, submitFileHandler)
	}
}

/* We need this value so that entryTable doesnt break when we try to change name cell from a input to a textBlock */
const entryTableReset = ref(false)

const requestIbanInformationHandler = async rowId => {
    showOverlayLoader.value = true
    batchfileEntryError.value[rowId].invalidIban = false

    const payload = {
        genericDestination: batchfileEntries.value[rowId].iban,
        transferType: 'INTRABANK'
    }

    try {
        const res = await requestIbanInformation(payload)

        if (res.data.data.name && res.data.data.name.length) {
            batchfileEntries.value[rowId].name = res.data.data.name
            batchfileEntries.value[rowId].validatedIban = true
            entryTableReset.value = !entryTableReset.value
        } else {
            batchfileEntries.value[rowId].validatedIban = false
            entryTableReset.value = !entryTableReset.value
        }

        /* We need this to give "time" to entryTable to re-render with new cell types */
        setTimeout(() => {
            showOverlayLoader.value = false
        }, 80)
    } catch (error) {
        showOverlayLoader.value = false
        batchfileEntries.value[rowId].validatedIban = false
        batchfileEntryError.value[rowId].iban = true
        batchfileEntryError.value[rowId].invalidIban = true
        entryTableReset.value = !entryTableReset.value
    }
}

const submitBatchfileHeader = async () => {
    showOverlayLoader.value = true

    const payload = {
        fileName: formData.value.fileName, // String
        transferDescription: formData.value.transferDescription, // String
        operationTypeDescription: formData.value.operationType, // String
        processingDate: formData.value.processDate, // Date
        fileType: formData.value.fileType, // String
        originAccountId: formData.value.selectedAccountId, // long
        sourceReference: formData.value.reference, // String
        ignoreCompanyCode: true
    }

    try {
        const response = await submitManualBatchFileHeader(payload)
        batchFileSubmissionId.value = response.data.data.id
        submitBatchfileBody()
	} catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.response.data, submitBatchfileHeader)
	}
}

const submitBatchfileBody = async () => {
    showOverlayLoader.value = true
    const payload = {
        batchFileSubmissionId: batchFileSubmissionId.value,
        ignoreCompanyCode: true,
        extendedEntries: createFileBody()
    }

    try {
        const response = await submitManualBatchFileEntries(payload)

        batchFileSubmissionEntriesResponse.value = response.data.data

        batchfileEntries.value = fileEntriesHandler(batchFileSubmissionEntriesResponse.value.fileEntries)

        validateRowsData()

        showOverlayLoader.value = false

        if (!batchFileSubmissionEntriesResponse.value.fileEntries.find(obj => obj.errorCode)) {
            activeStep.value++
        }
	} catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.response.data, submitBatchfileBody)
	}
}

const requestBatchTransfer = async () => {
    showOverlayLoader.value = true
    const payload = {
        id: batchFileSubmissionId.value
    }

    try {
         await aproveBatchfileTranfer(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        showOverlayLoader.value = false
        errorHandler(error.response.data, requestBatchTransfer)
	}
}

/*************************************************
*                                                *
*              Error/Success Handler             *
*                                                *
*************************************************/
const applyErrorState = ref(false)
const showFeedbackToast = ref(false)

const responseData = ref(null)
const feedbackOperationData = ref(null)
const isFavorite = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    responseData.value = {
        ...data.data,
        favoritable: data.data.approveStepCustomerOperation?.favoritable,
        errorLevel: data.errorLevel,
        code: data.code
    }

    isFavorite.value = data.data.approveStepCustomerOperation?.favorite
    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        additionalMessage: data.additionalMessage || '',
        buttonsData: !data.data.operationHasReceipt ? null : {
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.id },
            downloadUrl: createBatchfileDownloadReceiptUrl(data.data.operationId),
            multipleDownloadUrls: {
                downloadExcelUrl: createBatchfileExcelDownloadUrl({ params: { batchFileId: data.data.id } })
            },
            operationId: data.data.id
        }
    }

    activeStep.value = 4
}

const errorHandler = (data, retryFn, isChallengeError) => {
    showLoader.value = false
    showOverlayLoader.value = false

    const challengeErrorRightButtonText = data.code === 'wrong_challenge' ? t('commonButtons.newCode') : t('commonButtons.resolve')

	feedbackToastData.value = {
		toastType: data && data.errorLevel ? data.errorLevel.toLowerCase() : 'error',
		text: data && data.message ? data.message : t('genericErrorMessage'),
		buttons: [
			{ text: t('commonButtons.ignore') },
			{
                text: isChallengeError ? challengeErrorRightButtonText : t('commonButtons.tryAgain'),
                callbackAction: () => {
                    isChallengeError && data.code !== 'wrong_challenge' ? activeStep.value = 1 : retryFn()
                }
            }
		]
	}

	showFeedbackToast.value = true
}

const favoriteModalErrorHandler = (response) => {
    errorHandler(response)
    showFavoritesModal.value = false
}

const favoriteModalSuccessHandler = (response) => {
    isFavorite.value = !isFavorite.value
    feedbackToastData.value = sucessObjectConstrutor(response.message)
	showFeedbackToast.value = true
    showFavoritesModal.value = false
}

initialFetchs()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
